import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import COUNTRIES from "../../data/countries.json";
import { DIAL_CODES, flagEmoji } from "./dialCodes";
import { PHONE_LEN } from "./phoneLengths";

const SEL = "w-full rounded-2xl border border-border/50 bg-background px-4 py-2.5 text-sm shadow-neu-sm outline-none focus:ring-2 focus:ring-primary/40";
const BOX = "flex items-stretch overflow-hidden rounded-2xl border bg-background shadow-neu-sm transition-colors";

// حقلا الدولة والهاتف: الطول ثابت حسب الدولة — أحمر ناقص، أخضر مكتمل، ولا يقبل زيادة.
export const CountryPhone = ({ form, setForm }) => {
  const { t, i18n } = useTranslation();
  const isAr = (i18n.language || "ar").startsWith("ar");
  const options = useMemo(() => {
    const list = COUNTRIES
      .filter((c) => DIAL_CODES[c.code])
      .map((c) => ({ ...c, dial: DIAL_CODES[c.code], flag: flagEmoji(c.code) }));
    list.sort((a, b) => (isAr ? a.ar.localeCompare(b.ar, "ar") : a.en.localeCompare(b.en)));
    return list;
  }, [isAr]);
  const current = options.find((c) => c.code === form.country_code) || null;
  const expected = PHONE_LEN[form.country_code] || 9;
  const local = form.phone_local || "";
  const isValid = !!current && local.length === expected;
  const showRed = !!current && local.length > 0 && !isValid;
  const border = !current ? "border-border/50"
    : isValid ? "border-emerald-500 ring-2 ring-emerald-500/30"
    : showRed ? "border-destructive ring-2 ring-destructive/30"
    : "border-border/50";
  const chooseCountry = (code) => {
    const c = options.find((x) => x.code === code);
    setForm({ ...form, country_code: code, country: c ? (isAr ? c.ar : c.en) : "",
              phone_code: c ? c.dial : "", phone_local: "" });
  };
  const onLocalChange = (e) => {
    let v = e.target.value.replace(/[^\d]/g, "").replace(/^0+/, "");
    if (v.length > expected) v = v.slice(0, expected);
    setForm({ ...form, phone_local: v });
  };
  const counterTone = isValid ? "text-emerald-600" : showRed ? "text-destructive" : "text-muted-foreground";
  const placeholder = current ? "X".repeat(expected) : t("signup.selectCountryFirst");
  return (
    <>
      <div>
        <p className="label-xs mb-2">{t("signup.country")}</p>
        <select value={form.country_code || ""} onChange={(e) => chooseCountry(e.target.value)}
                required data-testid="signup-country" className={SEL}>
          <option value="">{t("signup.selectCountry")}</option>
          {options.map((c) => (
            <option key={c.code} value={c.code}>
              {c.flag} {isAr ? c.ar : c.en} ({c.dial})
            </option>
          ))}
        </select>
      </div>
      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="label-xs">{t("signup.phone")}</p>
          {current ? (
            <span data-testid="signup-phone-counter" className={`num text-xs font-bold ${counterTone}`}>
              {local.length}/{expected}
            </span>
          ) : null}
        </div>
        <div dir="ltr" className={`${BOX} ${border}`}>
          <span data-testid="signup-phone-code"
                className="num flex items-center gap-1.5 border-e border-border/60 bg-muted/40 px-3 py-2.5 font-mono text-sm font-bold text-primary select-none">
            <span className="text-base">{current?.flag || "🌍"}</span>
            <span>{form.phone_code || "+—"}</span>
          </span>
          <input type="tel" dir="ltr" data-testid="signup-phone-local"
                 disabled={!current} value={local} onChange={onLocalChange}
                 placeholder={placeholder} maxLength={expected}
                 className="num flex-1 bg-transparent px-3 py-2.5 font-mono text-sm outline-none disabled:cursor-not-allowed disabled:opacity-60" />
        </div>
        {showRed ? (
          <p data-testid="signup-phone-error" className="mt-1 text-xs font-semibold text-destructive">
            {t("signup.phoneLenError", { n: expected })}
          </p>
        ) : null}
      </div>
    </>
  );
};
